import { Card } from 'antd'
import { Building2, ChartNoAxesCombined, ReceiptText, Wallet } from 'lucide-react'
import type { ReportDimension, ReportSummary } from '@super-mega/server/contracts/analytics'
import { cn } from '@/utils/index.ts'
import { REPORT_LABELS, REPORT_METRIC_LABELS, REPORT_VIEWS, type ReportMetricKey } from './const/reports.ts'
import { MetricCard, MetricStat } from './metric-card.tsx'

const metricDescriptions: Partial<Record<ReportMetricKey, string>> = {
  ledgerBalance: '净收款 − 支出金额。支出按应付日期统计，用于台账收支对比，不代表已实际付款或会计净利润。',
  cashBalance: '净收款 − 实际付款 + 付款退回。正数为现金盈余，负数为现金缺口；包含押金等资金往来，不等同于会计净利润。',
  expense: '支出台账按应付日期统计；历史未转换的应付账单按出账及调整生效时间统计。',
  paid: '期间已确认付款，按实际付款发生时间统计。',
  paymentRefund: '期间实际收回的已确认付款退款，按退回发生时间统计。',
  refunded: '所选期间实际退回客户的金额，只计已确认退款；按退款发生时间统计。',
  net: '期间实际收款 − 期间实际退款。跨期退款计入退款当期，净收款可能为负。',
  received: '所选期间已确认的实际收款，未扣除退款。'
}

const metricIcons: Partial<Record<ReportMetricKey, typeof Building2>> = {
  activity: Building2,
  orders: ChartNoAxesCombined,
  activeRentals: ChartNoAxesCombined,
  billed: ReceiptText
}

export function ReportMetrics({
  data,
  dimension,
  overview
}: {
  data: ReportSummary
  dimension: ReportDimension
  overview?: boolean
}) {
  const view = REPORT_VIEWS[dimension]
  const primary: ReportMetricKey[] = overview ? ['net', 'expense', 'ledgerBalance'] : view.primary
  const primaryKeys = new Set(primary)
  const secondary: ReportMetricKey[] = overview
    ? ['received', 'refunded', 'billed', 'outstanding', 'orders', 'customers', 'subjects', 'activeRentals']
    : [...new Set<ReportMetricKey>([...view.secondary, 'received', 'refunded', 'net'])].filter(
        (key) => !primaryKeys.has(key)
      )
  function props(key: ReportMetricKey) {
    if (key === 'activity') {
      return {
        title: `下单${REPORT_LABELS[dimension]}数`,
        value: data.activity.current,
        previous: data.activity.previous,
        year: data.activity.year,
        description: '期间有新增订单的去重数量，不包含未归属记录。'
      }
    }
    if (key === 'outstanding') {
      return {
        title: REPORT_METRIC_LABELS[key],
        value: data.outstanding,
        money: true,
        description: '所选期间出账的账单，在本次查询时仍未收取的金额。'
      }
    }
    if (key === 'activeRentals') {
      return {
        title: REPORT_METRIC_LABELS[key],
        value: data.activeRentals,
        description: '租期与所选期间重叠的已启用租约，不等同于房间入住率。'
      }
    }
    return {
      title: REPORT_METRIC_LABELS[key],
      value: data.current[key],
      previous: data.previous[key],
      year: data.year[key],
      money: typeof data.current[key] === 'string',
      description: metricDescriptions[key]
    }
  }
  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {primary.map((key) => (
          <MetricCard
            key={key}
            {...props(key)}
            emphasis={key === 'net' ? 'accent' : undefined}
            icon={metricIcons[key] ?? Wallet}
          />
        ))}
      </div>
      <Card classNames={{ body: 'p-5 sm:p-6' }}>
        <div className={cn('grid grid-cols-2 gap-x-6 gap-y-6 md:grid-cols-3', overview && '2xl:grid-cols-6')}>
          {secondary.map((key) => (
            <MetricStat key={key} {...props(key)} />
          ))}
        </div>
      </Card>
    </>
  )
}
